/**
 * Shared ids and payload rules for Meta Conversions API and the GTM pixel.
 *
 * `sign_up` / `purchase` dataLayer pushes and the server events must use the
 * same id. Both sides derive it, so a lost browser session still matches:
 *   StartTrial → `start_trial_${userId}`
 *   Purchase   → `purchase_${subscriptionId}`
 *
 * Paddle Billing amounts are minor units. Meta `value` is a major-unit decimal.
 */

export const META_START_TRIAL_EVENT = "StartTrial" as const;
export const META_PURCHASE_EVENT = "Purchase" as const;
export const MARKETING_CONSENT_COOKIE = "orsane_marketing_consent";
export const DEFAULT_META_CURRENCY = "EUR";

export const ADS_QUERY_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "fbclid",
] as const;

export type AdsQueryKey = (typeof ADS_QUERY_KEYS)[number];
export type AdsParams = Partial<Record<AdsQueryKey, string>>;
export type MarketingConsent = "granted" | "denied";

const ZERO_DECIMAL_CURRENCIES = new Set([
    "BIF",
    "CLP",
    "DJF",
    "GNF",
    "JPY",
    "KMF",
    "KRW",
    "MGA",
    "PYG",
    "RWF",
    "UGX",
    "VND",
    "VUV",
    "XAF",
    "XOF",
    "XPF",
]);

const THREE_DECIMAL_CURRENCIES = new Set(["BHD", "JOD", "KWD", "OMR", "TND"]);

const FBP_PATTERN = /^fb\.\d+\.\d+\.\d+$/;
const FBC_PATTERN = /^fb\.\d+\.\d+\.[A-Za-z0-9_-]{1,200}$/;
const FBCLID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;

export function metaStartTrialEventId(userId: string): string {
    return `start_trial_${userId}`;
}

export function metaPurchaseEventId(subscriptionId: string): string {
    return `purchase_${subscriptionId}`;
}

/** Real Paddle subscription ids are `sub_…`. `"temp"` is the client placeholder. */
export function isRealPaddleSubscriptionId(id?: string | null): id is string {
    return typeof id === "string" && id.startsWith("sub_");
}

export function isMetaPixelId(value?: string | null): value is string {
    return typeof value === "string" && /^[0-9]{5,20}$/.test(value.trim());
}

export function normalizeMetaEmail(email?: string | null): string | undefined {
    if (!email) {
        return undefined;
    }
    const trimmed = email.trim().toLowerCase();
    return trimmed.length > 0 ? trimmed : undefined;
}

export function hasMarketingConsentFlag(value: unknown): boolean {
    return value === true || value === "true";
}

export function paddleMinorUnitFactor(currency: string): number {
    const code = currency.trim().toUpperCase();
    if (ZERO_DECIMAL_CURRENCIES.has(code)) {
        return 1;
    }
    if (THREE_DECIMAL_CURRENCIES.has(code)) {
        return 1000;
    }
    return 100;
}

/** Paddle Billing amounts are minor units. Meta `value` is a major-unit decimal. */
export function paddleMinorToMajor(amount: string | number, currency: string): number | undefined {
    const minor = typeof amount === "string" ? Number(amount) : amount;
    if (!Number.isFinite(minor)) {
        return undefined;
    }
    const factor = paddleMinorUnitFactor(currency);
    const decimals = factor === 1 ? 0 : factor === 1000 ? 3 : 2;
    const scale = 10 ** decimals;
    return Math.round((minor / factor) * scale) / scale;
}

export function sumPaddlePurchaseValue(
    items: Array<{ unitAmount?: string | number | null; quantity?: number | null }> | null | undefined,
    currency: string
): number | undefined {
    if (!items || items.length === 0) {
        return undefined;
    }
    let minor = 0;
    let any = false;
    for (const item of items) {
        const unit = typeof item.unitAmount === "string" ? Number(item.unitAmount) : item.unitAmount;
        if (typeof unit !== "number" || !Number.isFinite(unit)) {
            continue;
        }
        const quantity = typeof item.quantity === "number" && Number.isFinite(item.quantity) ? item.quantity : 1;
        minor += unit * quantity;
        any = true;
    }
    if (!any) {
        return undefined;
    }
    return paddleMinorToMajor(minor, currency);
}

/**
 * Meta `fbc` subdomain index: `example.com` = 1, `www.example.com` = 2.
 */
export function metaSubdomainIndex(hostname: string): number {
    const parts = hostname.split(".").filter(Boolean);
    return Math.max(0, parts.length - 1);
}

export function sanitizeFbclid(value?: string | null): string | undefined {
    if (!value) {
        return undefined;
    }
    const trimmed = value.trim();
    return FBCLID_PATTERN.test(trimmed) ? trimmed : undefined;
}

export function buildFbcFromFbclid(fbclid: string, creationTimeMs: number, subdomainIndex: number): string | undefined {
    const safe = sanitizeFbclid(fbclid);
    if (!safe || !Number.isFinite(creationTimeMs)) {
        return undefined;
    }
    return `fb.${subdomainIndex}.${Math.floor(creationTimeMs)}.${safe}`;
}

export function isMetaFbp(value?: string | null): value is string {
    return typeof value === "string" && FBP_PATTERN.test(value);
}

export function isMetaFbc(value?: string | null): value is string {
    return typeof value === "string" && FBC_PATTERN.test(value);
}

export function readCookie(cookieString: string | null | undefined, name: string): string | undefined {
    if (!cookieString) {
        return undefined;
    }
    for (const part of cookieString.split(";")) {
        const trimmed = part.trim();
        const eq = trimmed.indexOf("=");
        if (eq === -1) {
            continue;
        }
        if (trimmed.slice(0, eq) !== name) {
            continue;
        }
        const raw = trimmed.slice(eq + 1);
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }
    return undefined;
}

/** Grant only. Anything else, including a missing cookie, is not consent. */
export function parseMarketingConsent(cookieString?: string | null): MarketingConsent | null {
    const value = readCookie(cookieString, MARKETING_CONSENT_COOKIE);
    if (value === "granted" || value === "denied") {
        return value;
    }
    return null;
}

export function readAdsParams(search: string): AdsParams {
    const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    const out: AdsParams = {};
    for (const key of ADS_QUERY_KEYS) {
        const value = params.get(key)?.trim();
        if (!value || value.length > 200) {
            continue;
        }
        if (key === "fbclid" && !sanitizeFbclid(value)) {
            continue;
        }
        out[key] = value;
    }
    return out;
}

export type MetaMatch = {
    marketingConsent: true;
    fbp?: string;
    fbc?: string;
};

/**
 * fbp / fbc are returned only when the marketing-consent cookie is `granted`.
 * No cookie (the app today) yields null, so callers send no match keys.
 */
export function collectMetaMatch(input: {
    cookieString?: string | null;
    search?: string;
    nowMs?: number;
    hostname?: string;
}): MetaMatch | null {
    if (parseMarketingConsent(input.cookieString) !== "granted") {
        return null;
    }
    const fbp = readCookie(input.cookieString, "_fbp");
    const existingFbc = readCookie(input.cookieString, "_fbc");
    const fbclid = readAdsParams(input.search ?? "").fbclid;
    const built = fbclid
        ? buildFbcFromFbclid(fbclid, input.nowMs ?? Date.now(), metaSubdomainIndex(input.hostname ?? "my.orsane.app"))
        : undefined;
    const fbc = isMetaFbc(existingFbc) ? existingFbc : built;
    return {
        marketingConsent: true,
        ...(isMetaFbp(fbp) ? { fbp } : {}),
        ...(fbc && isMetaFbc(fbc) ? { fbc } : {}),
    };
}

export type MetaPurchaseSkipReason =
    | "not_subscription_event"
    | "placeholder_temp"
    | "not_real_subscription_id"
    | "already_real"
    | "not_active";

export type MetaPurchaseDecision =
    | { send: false; reason: MetaPurchaseSkipReason }
    | {
          send: true;
          eventId: string;
          subscriptionId: string;
          currency: string;
          value?: number;
      };

/**
 * First real payment only: a subscription webhook that replaces a missing or
 * `"temp"` id with an active `sub_…`. Renewals and the placeholder do not send.
 * Lack of consent does not drop the event; it only strips match keys later.
 */
export function decideMetaPurchase(input: {
    eventType?: string | null;
    subscriptionId?: string | null;
    previousSubscriptionId?: string | null;
    status?: string | null;
    currencyCode?: string | null;
    items?: Array<{ unitAmount?: string | number | null; quantity?: number | null }> | null;
}): MetaPurchaseDecision {
    if (input.eventType !== "subscription.created" && input.eventType !== "subscription.updated") {
        return { send: false, reason: "not_subscription_event" };
    }
    const subscriptionId = input.subscriptionId;
    if (subscriptionId === "temp") {
        return { send: false, reason: "placeholder_temp" };
    }
    if (!isRealPaddleSubscriptionId(subscriptionId)) {
        return { send: false, reason: "not_real_subscription_id" };
    }
    if (isRealPaddleSubscriptionId(input.previousSubscriptionId)) {
        return { send: false, reason: "already_real" };
    }
    if (input.status !== "active") {
        return { send: false, reason: "not_active" };
    }
    const currency = (input.currencyCode || DEFAULT_META_CURRENCY).trim().toUpperCase() || DEFAULT_META_CURRENCY;
    const value = sumPaddlePurchaseValue(input.items, currency);
    return {
        send: true,
        eventId: metaPurchaseEventId(subscriptionId),
        subscriptionId,
        currency,
        ...(typeof value === "number" ? { value } : {}),
    };
}

/** Identifiers are omitted entirely unless marketing consent was granted. */
export function buildMetaUserData(input: {
    marketingConsent: boolean;
    hashedEmail?: string | null;
    fbp?: string | null;
    fbc?: string | null;
    clientIp?: string | null;
    userAgent?: string | null;
}): Record<string, string | string[]> {
    if (!input.marketingConsent) {
        return {};
    }
    const data: Record<string, string | string[]> = {};
    if (input.hashedEmail) {
        data.em = [input.hashedEmail];
    }
    if (isMetaFbp(input.fbp)) {
        data.fbp = input.fbp;
    }
    if (isMetaFbc(input.fbc)) {
        data.fbc = input.fbc;
    }
    if (input.clientIp && input.clientIp.length > 0 && input.clientIp.length <= 64) {
        data.client_ip_address = input.clientIp;
    }
    if (input.userAgent && input.userAgent.length > 0 && input.userAgent.length <= 512) {
        data.client_user_agent = input.userAgent;
    }
    return data;
}

export function safeHttpUrl(value?: string | null): string | undefined {
    if (!value) {
        return undefined;
    }
    try {
        const url = new URL(value);
        if (url.protocol !== "https:" && url.protocol !== "http:") {
            return undefined;
        }
        return url.toString().slice(0, 1000);
    } catch {
        return undefined;
    }
}

export function buildCapiEvent(input: {
    eventName: typeof META_START_TRIAL_EVENT | typeof META_PURCHASE_EVENT;
    eventId: string;
    eventTime: number;
    eventSourceUrl?: string;
    userData: Record<string, string | string[]>;
    customData?: Record<string, string | number>;
}): Record<string, unknown> {
    return {
        event_name: input.eventName,
        event_time: input.eventTime,
        event_id: input.eventId,
        action_source: "website",
        ...(input.eventSourceUrl ? { event_source_url: input.eventSourceUrl } : {}),
        user_data: input.userData,
        ...(input.customData && Object.keys(input.customData).length > 0 ? { custom_data: input.customData } : {}),
    };
}

export function buildCapiRequestBody(
    events: Array<Record<string, unknown>>,
    testEventCode?: string | null
): Record<string, unknown> {
    const body: Record<string, unknown> = { data: events };
    const code = testEventCode?.trim();
    if (code) {
        body.test_event_code = code;
    }
    return body;
}
